import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Eye, Search, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import useDraggable from '../hooks/useDraggable';
import { bubblePanelStyle } from '../utils/bubblePanel';
import { presenceStatus, presenceColor } from '../utils/presence';

// "Last online" pill for a user row. Status/color come from the shared presence model so this
// agrees with the dot everywhere else. Agents carry a computed `presence` + `last_seen_at`;
// clients carry `last_activity_at`/`last_login`.
function lastOnline(u, t) {
  const status = presenceStatus(u);
  const color = presenceColor(status);
  if (status === 'online') return { color, text: t('presence.online') };
  const ts = u.last_seen_at || u.last_activity_at || u.last_login || null;
  if (!ts) return { color, text: t('common.never') };
  const mins = Math.floor((Date.now() - new Date(ts).getTime()) / 60000);
  const text = mins < 60 ? t('presence.agoMin', { count: mins })
    : mins < 1440 ? t('presence.agoHour', { count: Math.floor(mins / 60) })
    : t('presence.agoDay', { count: Math.floor(mins / 1440) });
  return { color, text };
}

// Sort key for "most recently online first": online users rank highest, then by
// last-seen timestamp descending; "never seen" sinks to the bottom. Uses the same
// presence model + timestamp fallbacks as the row pill so the order matches the dots.
function onlineRank(u) {
  if (presenceStatus(u) === 'online') return Number.MAX_SAFE_INTEGER;
  const ts = u.last_seen_at || u.last_activity_at || u.last_login || null;
  return ts ? new Date(ts).getTime() : 0;
}
const byLastOnline = (a, b) => onlineRank(b) - onlineRank(a);

const AdminViewAsWidget = () => {
  const { t } = useTranslation();
  const { isAdmin, isImpersonating, startImpersonation, user } = useAuth();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [agents, setAgents] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const popoverRef = useRef(null);
  const inputRef = useRef(null);

  const { pos: dragPos, isDragging, wasDragged, handlers: dragHandlers, elementRef: fabRef } = useDraggable('admin-viewas-pos', { defaultZone: 'bottom-left', width: 44, height: 44 });

  if (!isAdmin || isImpersonating) return null;

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const [agentsRes, clientsRes] = await Promise.all([
        api.get('/agents'),
        api.get('/companies/portal-users/all').catch(() => ({ data: { users: [] } })),
      ]);
      const agentList = (agentsRes.data.agents || agentsRes.data || []).filter(a => a.id !== user.id);
      const clientList = clientsRes.data.users || clientsRes.data || [];
      // Most recently online first (online users on top), matching the last-online pill.
      agentList.sort(byLastOnline);
      clientList.sort(byLastOnline);
      setAgents(agentList);
      setClients(clientList);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = () => {
    setOpen(true);
    setSearch('');
    fetchUsers();
  };

  const handleImpersonate = async (targetId) => {
    try {
      await startImpersonation(targetId);
      setOpen(false);
    } catch (err) {
      toast.error(t('impersonation.startError'));
    }
  };

  const filter = (list) => {
    if (!search.trim()) return list;
    const q = search.toLowerCase();
    return list.filter(u =>
      (u.first_name || u.firstName || '').toLowerCase().includes(q) ||
      (u.last_name || u.lastName || '').toLowerCase().includes(q) ||
      (u.email || '').toLowerCase().includes(q)
    );
  };

  const filteredAgents = filter(agents);
  const filteredClients = filter(clients);

  const getName = (u) => `${u.first_name || u.firstName || ''} ${u.last_name || u.lastName || ''}`.trim() || u.email;

  return (
    <>
      {/* Floating trigger button */}
      <button
        ref={fabRef}
        onClick={() => { if (!wasDragged.current) handleOpen(); }}
        title={t('impersonation.viewAs')}
        style={{
          position: 'fixed',
          left: dragPos.x, top: dragPos.y, right: 'auto', bottom: 'auto',
          zIndex: 9990,
          width: 44,
          height: 44,
          borderRadius: '50%',
          background: 'var(--primary, #6366f1)',
          color: '#fff',
          border: 'none',
          cursor: isDragging ? 'grabbing' : 'grab',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
          transition: isDragging ? 'none' : 'left 0.18s ease, top 0.18s ease',
          touchAction: 'none',
        }}
        {...dragHandlers}
      >
        <Eye size={22} />
      </button>

      {/* Popover */}
      {open && (
        <div
          ref={popoverRef}
          style={{
            position: 'fixed',
            ...(window.innerWidth < 768
              // En el celular, hoja ancha sobre la barra inferior (el dock la deja libre).
              ? { left: 8, right: 8, width: 'auto', bottom: 'calc(var(--mobile-bottom-nav, 0px) + var(--safe-bottom, 0px) + 72px)', maxHeight: '60vh' }
              : (() => {
                  const ps = bubblePanelStyle(dragPos, { width: 320, height: 420, fabSize: 44, minHeight: 200 });
                  return { ...ps, width: 320, maxHeight: Math.min(420, ps.maxHeight || 420) };
                })()),
            zIndex: 9991,
            background: 'var(--bg-card, #fff)',
            border: '1px solid var(--border, #e5e7eb)',
            borderRadius: 10,
            boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <div style={{
            padding: '10px 12px',
            borderBottom: '1px solid var(--border, #e5e7eb)',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}>
            <Eye size={16} style={{ color: 'var(--primary, #6366f1)' }} />
            <span style={{ fontWeight: 600, fontSize: 14, flex: 1 }}>{t('impersonation.viewAs')}</span>
            <button onClick={() => setOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2 }}>
              <X size={16} style={{ color: 'var(--text-secondary, #6b7280)' }} />
            </button>
          </div>

          {/* Search */}
          <div style={{ padding: '8px 12px', borderBottom: '1px solid var(--border, #e5e7eb)' }}>
            <div style={{ position: 'relative' }}>
              <Search size={14} style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
              <input
                ref={inputRef}
                autoFocus
                type="text"
                placeholder={t('impersonation.searchUsers')}
                value={search}
                onChange={e => setSearch(e.target.value)}
                style={{
                  width: '100%',
                  padding: '6px 8px 6px 28px',
                  border: '1px solid var(--border, #e5e7eb)',
                  borderRadius: 6,
                  fontSize: 13,
                  background: 'var(--bg-input, #fff)',
                  color: 'var(--text, #111)',
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
              />
            </div>
          </div>

          {/* User list */}
          <div style={{ overflow: 'auto', flex: 1, padding: '4px 0' }}>
            {loading ? (
              <div style={{ padding: 16, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>
                {t('common.loading')}
              </div>
            ) : (
              <>
                {filteredAgents.length > 0 && (
                  <>
                    <div style={{ padding: '6px 12px', fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      {t('impersonation.agents')}
                    </div>
                    {filteredAgents.map(u => (
                      <button
                        key={u.id}
                        onClick={() => handleImpersonate(u.id)}
                        style={{
                          width: '100%',
                          padding: '6px 12px',
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          fontSize: 13,
                          color: 'var(--text, #111)',
                          textAlign: 'left',
                        }}
                        onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover, #f3f4f6)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'none'}
                      >
                        {u.avatar_url || u.avatarUrl ? (
                          <img src={u.avatar_url || u.avatarUrl} alt="" style={{ width: 24, height: 24, borderRadius: '50%' }} />
                        ) : (
                          <div style={{ width: 24, height: 24, borderRadius: '50%', background: '#e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 600 }}>
                            {(u.first_name || u.firstName || u.email || '?')[0].toUpperCase()}
                          </div>
                        )}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{getName(u)}</div>
                          <div style={{ fontSize: 11, color: '#9ca3af', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.email}</div>
                        </div>
                        {(() => { const lo = lastOnline(u, t); return (
                          <span title={`Última vez: ${lo.text}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 10, color: '#6b7280', whiteSpace: 'nowrap' }}>
                            <span style={{ width: 7, height: 7, borderRadius: '50%', background: lo.color }} />{lo.text}
                          </span>
                        ); })()}
                        <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 4, background: '#e0e7ff', color: '#4338ca', fontWeight: 600 }}>{u.role}</span>
                      </button>
                    ))}
                  </>
                )}

                {filteredClients.length > 0 && (
                  <>
                    <div style={{ padding: '6px 12px', fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 0.5, marginTop: filteredAgents.length > 0 ? 4 : 0 }}>
                      {t('impersonation.clients')}
                    </div>
                    {filteredClients.map(u => (
                      <button
                        key={u.id}
                        onClick={() => handleImpersonate(u.id)}
                        style={{
                          width: '100%',
                          padding: '6px 12px',
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          fontSize: 13,
                          color: 'var(--text, #111)',
                          textAlign: 'left',
                        }}
                        onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover, #f3f4f6)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'none'}
                      >
                        {u.avatar_url || u.avatarUrl ? (
                          <img src={u.avatar_url || u.avatarUrl} alt="" style={{ width: 24, height: 24, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
                        ) : (
                          <div style={{ width: 24, height: 24, borderRadius: '50%', background: '#fef3c7', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 600, color: '#92400e', flexShrink: 0 }}>
                            {(u.first_name || u.firstName || u.email || '?')[0].toUpperCase()}
                          </div>
                        )}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{getName(u)}</div>
                          <div style={{ fontSize: 11, color: '#9ca3af', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.email}</div>
                        </div>
                        {(() => { const lo = lastOnline(u, t); return (
                          <span title={`Última vez: ${lo.text}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 10, color: '#6b7280', whiteSpace: 'nowrap' }}>
                            <span style={{ width: 7, height: 7, borderRadius: '50%', background: lo.color }} />{lo.text}
                          </span>
                        ); })()}
                        <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 4, background: '#fef3c7', color: '#92400e', fontWeight: 600 }}>client</span>
                      </button>
                    ))}
                  </>
                )}

                {filteredAgents.length === 0 && filteredClients.length === 0 && !loading && (
                  <div style={{ padding: 16, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>
                    {t('common.noResults', 'No results')}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Backdrop */}
      {open && (
        <div
          onMouseDown={(e) => { if (e.target === e.currentTarget) { (() => setOpen(false))(e); } }}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 9989,
            background: 'transparent',
          }}
        />
      )}
    </>
  );
};

export default AdminViewAsWidget;
